import {
    type ReactNode,
    type RefObject,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
} from 'react';
import { createPortal } from 'react-dom';

import { AgentApprovalModeControlRendererProvider } from '@apps/web/components/phoebe_chat/agent_approval_mode_control';
import {
    type AgentModelPickerProps,
    AgentModelPickerRendererProvider,
} from '@apps/web/components/phoebe_chat/agent_model_picker';
import {
    type AgentTestMode,
    type RawToolDescriptionMode,
} from '@apps/web/components/phoebe_chat/agent_test_mode';
import { type TranscriptEntry } from '@apps/web/components/phoebe_chat/transcript_types';
import { type V3UiContextSnapshot } from '@apps/web/components/phoebe_chat/v3_ui_context';
import { ScrollArea } from '@apps/web/components/ui/scroll_area';
import { useV3LiveDeltas } from '@apps/web/features/phoebe_chat_v3/api/use_live_deltas';
import { type V3ChatActions } from '@apps/web/features/phoebe_chat_v3/model/actions';
import { filterCommittedLiveEntries } from '@apps/web/features/phoebe_chat_v3/model/live_delta_projection';
import { V3RunStore } from '@apps/web/features/phoebe_chat_v3/model/run_store';
import {
    hasVisibleAssistantAnswer,
    latestTurnWorkingState,
    resolveV3WorkingStatus,
} from '@apps/web/features/phoebe_chat_v3/model/working_status';
import { V3AgentToolbar } from '@apps/web/features/phoebe_chat_v3/ui/agent_toolbar';
import { V3EmptyState } from '@apps/web/features/phoebe_chat_v3/ui/ambient_components';
import { V3ApprovalModeControl } from '@apps/web/features/phoebe_chat_v3/ui/approval_mode_control';
import { RawModeBanner } from '@apps/web/features/phoebe_chat_v3/ui/raw_mode_banner';
import { V3Transcript } from '@apps/web/features/phoebe_chat_v3/ui/transcript';
import { V3UiContextProvider } from '@apps/web/features/phoebe_chat_v3/ui/ui_context_provider';
import {
    useV3ChatPerformance,
    type V3ChatPerformanceAttempt,
} from '@apps/web/features/phoebe_chat_v3/ui/use_chat_performance';
import { useStableV3ChatActions } from '@apps/web/features/phoebe_chat_v3/ui/use_stable_chat_actions';
import { useV3StickyScroll } from '@apps/web/features/phoebe_chat_v3/ui/use_sticky_scroll';
import { useV3TranscriptAdapter } from '@apps/web/features/phoebe_chat_v3/ui/use_transcript_adapter';
import { V3WorkingRow } from '@apps/web/features/phoebe_chat_v3/ui/working_row';
import { cn } from '@apps/web/lib/utils';
import { type Mode } from '@libraries/typescript/api_client';

const ignoreAgentTestModeChange = () => undefined;

export function V3ChatSurface({
    runId,
    liveDeltasEnabled,
    entries,
    organizationId,
    organizationTimezone,
    composer,
    composerFooter = null,
    composerPortalTarget = null,
    composerReady,
    composerReadySentRef,
    isBusy,
    starterPrompts,
    canSelectStarterPrompt,
    onStarterPromptSelect,
    mode,
    actions,
    onPerformanceAttemptCompleted,
    performanceAttempt,
    surface,
    surfaceStartedAtMs,
    agentTestMode = 'normal',
    rawToolDescriptions = 'stripped',
    allowParallelAgentTestMode = false,
    agentTestModeDisabled = false,
    onAgentTestModeChange = ignoreAgentTestModeChange,
    onRawToolDescriptionsChange,
    showAgentTestModeControl = false,
    onUiContextSnapshotChange = () => undefined,
}: {
    readonly runId: string | null;
    readonly liveDeltasEnabled: boolean;
    readonly entries: ReadonlyArray<TranscriptEntry>;
    readonly organizationId: string | null;
    readonly organizationTimezone: string;
    readonly composer: ReactNode;
    readonly composerFooter?: ReactNode;
    readonly composerPortalTarget?: HTMLElement | null;
    readonly composerReady: boolean;
    readonly composerReadySentRef: RefObject<boolean>;
    readonly isBusy: boolean;
    readonly starterPrompts: ReadonlyArray<string>;
    readonly canSelectStarterPrompt: boolean;
    readonly onStarterPromptSelect: (
        prompt: string,
    ) => Promise<boolean> | boolean;
    readonly mode: Mode;
    readonly actions: V3ChatActions;
    readonly onPerformanceAttemptCompleted: (attemptId: number) => void;
    readonly performanceAttempt: V3ChatPerformanceAttempt | null;
    readonly surface: 'extension' | 'web';
    readonly surfaceStartedAtMs: number;
    readonly agentTestMode?: AgentTestMode;
    readonly rawToolDescriptions?: RawToolDescriptionMode;
    readonly allowParallelAgentTestMode?: boolean;
    readonly agentTestModeDisabled?: boolean;
    readonly onAgentTestModeChange?: (mode: AgentTestMode) => void;
    readonly onRawToolDescriptionsChange?: (
        mode: RawToolDescriptionMode,
    ) => void;
    readonly showAgentTestModeControl?: boolean;
    readonly onUiContextSnapshotChange?: (
        snapshot: V3UiContextSnapshot | null,
    ) => void;
}) {
    const store = useMemo(() => new V3RunStore(), []);
    const renderAgentToolbar = useCallback(
        (props: AgentModelPickerProps) => (
            <V3AgentToolbar
                {...props}
                agentTestMode={agentTestMode}
                rawToolDescriptions={rawToolDescriptions}
                allowParallelAgentTestMode={allowParallelAgentTestMode}
                agentTestModeDisabled={agentTestModeDisabled}
                onAgentTestModeChange={onAgentTestModeChange}
                onRawToolDescriptionsChange={onRawToolDescriptionsChange}
                showAgentTestModeControl={showAgentTestModeControl}
            />
        ),
        [
            agentTestMode,
            agentTestModeDisabled,
            allowParallelAgentTestMode,
            onAgentTestModeChange,
            onRawToolDescriptionsChange,
            rawToolDescriptions,
            showAgentTestModeControl,
        ],
    );
    const adapter = useV3TranscriptAdapter();
    const projection = useMemo(
        () => adapter.adapt(entries),
        [adapter, entries],
    );
    const normalizedEntries = projection.entries;
    const presentationContext = useMemo(
        () =>
            organizationId === null
                ? null
                : {
                      organizationId,
                      organizationTimezone,
                      mode,
                  },
        [mode, organizationId, organizationTimezone],
    );
    const liveDeltaStream = useV3LiveDeltas({
        organizationId,
        runId,
        mode,
        enabled: liveDeltasEnabled && isBusy,
    });
    const liveEntries = liveDeltaStream.entries;
    const committedAssistantItemIds = useMemo(
        () =>
            new Set(
                normalizedEntries.flatMap((entry) =>
                    entry.kind === 'message' &&
                    entry.role === 'assistant' &&
                    entry.sourceItemId
                        ? [entry.sourceItemId]
                        : [],
                ),
            ),
        [normalizedEntries],
    );
    const turnBaselineAssistantItemIdsRef = useRef<ReadonlySet<string>>(
        new Set(),
    );
    const previousLiveTurnRef = useRef({ isBusy: false, runId });
    useEffect(() => {
        const previousLiveTurn = previousLiveTurnRef.current;
        if (
            isBusy &&
            (!previousLiveTurn.isBusy || previousLiveTurn.runId !== runId)
        ) {
            turnBaselineAssistantItemIdsRef.current = committedAssistantItemIds;
        }
        previousLiveTurnRef.current = { isBusy, runId };
    }, [committedAssistantItemIds, isBusy, runId]);
    const visibleLiveEntries = useMemo(
        () =>
            filterCommittedLiveEntries(normalizedEntries, liveEntries, {
                isBusy,
                baselineDurableAssistantItemIds:
                    turnBaselineAssistantItemIdsRef.current,
            }),
        [isBusy, liveEntries, normalizedEntries],
    );
    const displayedEntryCount =
        normalizedEntries.length + visibleLiveEntries.length;
    const userMessageCount = useMemo(
        () =>
            normalizedEntries.filter(
                (entry) => entry.kind === 'message' && entry.role === 'user',
            ).length,
        [normalizedEntries],
    );
    const { scrollAreaRef, contentRef } = useV3StickyScroll({
        entryCount: displayedEntryCount,
        isBusy,
        userMessageCount,
    });
    const stableActions = useStableV3ChatActions({
        actions,
        onPromptSubmit: canSelectStarterPrompt
            ? onStarterPromptSelect
            : undefined,
    });
    const composerContainerRef = useRef<HTMLDivElement>(null);
    const assistantRevisions = useMemo(
        () =>
            [...normalizedEntries, ...visibleLiveEntries].flatMap((entry) =>
                entry.kind === 'message' &&
                entry.role === 'assistant' &&
                entry.content.length > 0
                    ? [`${entry.id}:${entry.revision}`]
                    : [],
            ),
        [normalizedEntries, visibleLiveEntries],
    );
    useV3ChatPerformance({
        assistantRevisions,
        attempt: performanceAttempt,
        composerReady,
        composerContainerRef,
        composerReadySentRef,
        isBusy,
        liveStreamErrorCount: liveDeltaStream.errorCount,
        mode,
        onAttemptCompleted: onPerformanceAttemptCompleted,
        organizationId,
        sourceVersion: liveDeltasEnabled ? 'v3' : 'v2',
        surface,
        surfaceStartedAtMs,
    });
    useLayoutEffect(() => {
        store.replace(normalizedEntries);
    }, [normalizedEntries, store]);
    useLayoutEffect(() => {
        store.replaceLive(visibleLiveEntries);
    }, [store, visibleLiveEntries]);

    const hasVisibleAnswer = hasVisibleAssistantAnswer({
        durableEntries: normalizedEntries,
        liveEntries: visibleLiveEntries,
    });
    const workingTurn = useMemo(
        () => latestTurnWorkingState(normalizedEntries),
        [normalizedEntries],
    );
    const completedWorkingSteps = workingTurn.milestones;
    const workingStatus = resolveV3WorkingStatus({
        hasVisibleAnswer,
        activeStatus: projection.activeStatus,
        liveReasoningTitle: liveDeltaStream.reasoningTitle,
        lastMilestoneLabel:
            completedWorkingSteps[completedWorkingSteps.length - 1]?.label ??
            null,
    });
    const composerToolbar = composer ? (
        <div
            ref={composerContainerRef}
            className="mx-auto w-full max-w-[792px] shrink-0 px-4 pt-2 pb-4"
        >
            <AgentModelPickerRendererProvider render={renderAgentToolbar}>
                {composer}
            </AgentModelPickerRendererProvider>
            <AgentApprovalModeControlRendererProvider
                render={V3ApprovalModeControl}
            >
                {composerFooter}
            </AgentApprovalModeControlRendererProvider>
        </div>
    ) : null;
    const showWorkingTrace = isBusy;

    return (
        <V3UiContextProvider onSnapshotChange={onUiContextSnapshotChange}>
            <div
                data-testid="v3-chat-surface"
                data-agent-test-mode={agentTestMode}
                className={cn(
                    'flex h-full max-h-full min-h-0 flex-1 flex-col overflow-hidden',
                    agentTestMode === 'raw' &&
                        'ring-2 ring-inset ring-amber-500',
                )}
            >
                {agentTestMode === 'raw' ? (
                    <RawModeBanner toolDescriptions={rawToolDescriptions} />
                ) : null}
                <ScrollArea
                    ref={scrollAreaRef}
                    data-testid="v3-transcript-scroll-area"
                    viewportClassName="[&>div]:!block"
                    // Zero intrinsic height plus flex-1 keeps long answers inside the panel.
                    className="flex h-0 min-h-0 flex-1 flex-col"
                >
                    <div ref={contentRef}>
                        {displayedEntryCount === 0 ? (
                            <V3EmptyState
                                prompts={starterPrompts}
                                disabled={!canSelectStarterPrompt}
                                onPromptSelect={onStarterPromptSelect}
                            />
                        ) : (
                            <V3Transcript
                                store={store}
                                presentationContext={presentationContext}
                                actions={stableActions}
                                hideThisTurnWork={showWorkingTrace}
                            />
                        )}
                        {showWorkingTrace ? (
                            <div className="mx-auto w-full max-w-[792px] px-4 pb-4">
                                <V3WorkingRow
                                    key={workingTurn.key}
                                    status={workingStatus}
                                    completedSteps={completedWorkingSteps}
                                />
                            </div>
                        ) : null}
                    </div>
                </ScrollArea>
                {composerPortalTarget === null ? composerToolbar : null}
            </div>
            {composerPortalTarget !== null && composerToolbar !== null
                ? createPortal(composerToolbar, composerPortalTarget)
                : null}
        </V3UiContextProvider>
    );
}
